import {
  IStyles,
  pxTovW,
  pxToRem,
  Loader,
  PrimaryButton,
  deserify,
  useCommonServiceClientContext,
  getLocalStorage,
  ActionsPopup,
  cloneObject,
} from '@geneo2-web/shared-ui';

import { Box, Tooltip, Typography } from '@mui/material';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../../reduxStore/reduxHooks';
import { useEffect, useState } from 'react';
import {
  ISteps,
  StepperComponent,
} from '../../QuestionPaperGenerator/QuestionPaperCreate/components/Stepper';
import { QPGCreationChapterSelection } from './components/QPGCreationChapterSelection';
import { QPGCreationSettings } from './components/QPGCreationSettings';
import { QPGCreationAddQuestionType } from './components/QPGCreationAddQuestionType';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  setToastInfo,
  setToastInfoV2,
} from '../../../Home/reducer/homeDashboard.slice';
import { ConnectError } from '@bufbuild/connect';
import {
  AssessmentCreationEnum,
  SubSectionQuestionType,
  SubSectionTypeV2,
  TaskInfoModel,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { getDifficultyLevelFromString } from '../../Common/ReviewAssessment/functions';
import {
  HOME,
  QPG_ASSESSMENT_PUBLISH,
  QPG_ASSESSMENT_REVIEW_PAPER,
} from '../../../../routeHandling/RoutesNomenclature';
import {
  setQPGContent,
  setQPGCreatedSectionTest,
  setQPGSelectedChapterData,
} from '../../reducer/assessmentFlow.slice';

import {
  ChapterIdsInfo,
  ChapterInfo,
  CloudUpload,
  MultiSubjectSection,
  MultiSubjectSectionStatusEnum,
  MultiSubjectSubSection,
  QpgSubsectionTypeEnum,
  QuestionModuleMap,
  QuestionTypeMarks,
  SectionQuestionMetaData,
  TaskCreationStatusEnum,
  TaskEnum,
} from '@protos/learning_management/lms.db_pb';
import {
  areAllSubSectionQuestionsFilled,
  buildTaskInfoModels,
  getEffectiveMarksForSubSectionQuestion,
  getQpgCreationSubSectionMarksTotal,
  getUniqueQuestionIdsFromQpgContent,
  isPassageSectionEnabled,
  isPassageTextMinLengthMet,
  mergeChapterTopicsInfoUnion,
  toRequestMarks,
  withSeededGroupedQuestionHeading,
} from '../../utils/QPGv2.utils';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import {
  CourseIndexInfo,
  SubjectChapterMiniInfo,
  TopicBasicInfo,
} from '@protos/content_management/content.common.apis_pb';
import { Question } from '@protos/content_management/content.db_pb';
import { getUpdatedSectionChapterInfoFromQuestionModuleMap } from '../../FormativeAssessmentV2/FAv2.utils';

const styles: IStyles = {
  root: {
    paddingX: { xs: pxToRem(20), md: pxTovW(50), lg: pxTovW(240) },
    paddingTop: 0,
    paddingBottom: { xs: pxToRem(85), md: pxTovW(50) },
  },
  buttonContainer: {
    position: { xs: 'fixed', md: 'unset' },
    top: { xs: '92%', md: 'unset' },
    width: { xs: '90%', md: pxTovW(397) },
    height: { xs: pxToRem(50), md: pxTovW(80), lg: pxTovW(60) },
    marginLeft: 'auto',
    zIndex: { xs: 1, md: 0 },
  },
};

export const QPGCreation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const teacherId = getLocalStorage('userId');
  const [searchParams] = useSearchParams();
  const subjectId = searchParams.get('subject_id');
  const editFlow = searchParams.get('edit_flow');
  const { setSelectedFunction, setSelectedLogoClickHandler } =
    useGlobalContext();
  const {
    LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV7WithStatusCodeHandler,
    LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const user_info = useDeserifiedSelector((state) => state.auth.user_info);
  const {
    qpg_selected_chapter_data,
    qpg_settings_config,
    qpg_subsection_data,
    qpg_content,
    qpg_selected_section_index,
    qpg_selected_section_number,
    qpg_format_details,
  } = deserify(useAppSelector((state) => state.assessment));

  const [selectedStepIndex, setSelectedStepIndex] = useState<number>(
    editFlow === 'chapter'
      ? 0
      : editFlow === 'bloom'
      ? 1
      : editFlow === 'ques-type'
      ? 2
      : 0
  );
  const [loading, setLoading] = useState<'loading' | 'completed' | 'error'>(
    'completed'
  );
  const [isTemplateValid, setIsTemplateValid] = useState<boolean>(false);
  const [confirmCreateAssessmentPopup, setConfirmCreateAssessmentPopup] =
    useState<boolean>(false);
  const [exitConfirmPopup, setExitConfirmPopup] = useState<
    'back' | 'logo' | undefined
  >(undefined);

  // --------------------------------------------------------------

  const createTestHandler = async () => {
    // Rebuild protobuf messages from Redux plain objects. Mutating API response
    // messages with deserified nested fields (e.g. CloudUpload) breaks Connect
    // serialization (.toJson is not a function).
    const modifiedSubSectionRequest = (qpg_subsection_data ?? []).map((sub) => {
      const passageImagesUpload = sub.isAiGeneratedPassage
        ? []
        : (sub.passageImagesUpload ?? []).map(
            (upload) => new CloudUpload(upload)
          );
      const attemptAnyEnabled = sub.attemptAnyCount !== undefined;
      const subSectionQuestions = (sub.subSectionQuestions ?? []).map(
        (question) => {
          const { perChildMarks } = getEffectiveMarksForSubSectionQuestion({
            marks: question.marks ?? 0,
            noOfQuestion: question.noOfQuestion ?? 0,
            isGrouped: question.isGrouped,
            attemptAnyEnabled,
            groupQuestionAttemptedAny: question.groupQuestionAttemptedAny,
          });
          return new SubSectionQuestionType({
            ...question,
            // API expects per-question marks; grouped passage attempt-any UI stores group total.
            marks: toRequestMarks(perChildMarks),
            ...(question.groupQuestionHeading !== undefined
              ? { groupQuestionHeading: question.groupQuestionHeading }
              : {}),
            ...(question.groupQuestionAttemptedAny !== undefined
              ? {
                  groupQuestionAttemptedAny: question.groupQuestionAttemptedAny,
                }
              : {}),
          });
        }
      );
      return new SubSectionTypeV2({
        ...sub,
        subSectionQuestions,
        ...(sub.isAiGeneratedPassage
          ? { passageText: [], passageImagesUpload: [] }
          : { passageImagesUpload }),
      });
    });

    try {
      setLoading('loading');
      const getModuleQuestionsWithFiltersResponse =
        await LmsAssessmentTeacherAPIServiceV8WithStatusCodeHandler.getModuleQuestionsWithFilters(
          {
            teacherId: BigInt(teacherId),
            subjectId:
              qpg_content?.multiSubjectSections[qpg_selected_section_index ?? 0]
                ?.subjectId,

            sectionId: qpg_format_details?.selectedSection?.id,
            // schoolId: user_info?.schoolDetails?.[0]?.schoolId,
            aiQuestionPrompts: qpg_settings_config?.prompt
              ? [qpg_settings_config.prompt]
              : [],
            allowAiQuestions: !!qpg_settings_config?.allow_ai_questions,
            assessmentCreationType:
              AssessmentCreationEnum.ASSESSMENT_CREATION_QUESTION_PAPER,
            avgDifficultyLevel: getDifficultyLevelFromString(
              qpg_settings_config?.overallDifficultyLevel || 'Medium'
            ),
            bloomLevels: qpg_settings_config?.bloomLevels || [],
            isAiAssessment: !!qpg_settings_config?.allow_ai_questions,
            moduleIds: qpg_selected_chapter_data?.chapters.flatMap(
              (chapter) => [
                chapter.chapterId,
                ...chapter.topics.map((topic) => topic.topicId),
              ]
            ),
            // approximateTime:
            // createdByUserId:
            // currentQuestionId:
            // modeOfConduction:
            // noOfQuestions:
            // pageNumber:
            // qIdsToBeIgnored:
            // questionSectionInfo:
            // requiredDifficultyLevelsCountInfo:
            // requiredDifficultyTypes:
            // requiredQuestionTypes:
            subSectionRequest: modifiedSubSectionRequest,
            qIdsToBeIgnored: getUniqueQuestionIdsFromQpgContent({
              qpgContent: qpg_content,
              sectionNumbersToIgnore:
                qpg_selected_section_number !== undefined
                  ? [qpg_selected_section_number]
                  : [],
            }),
          }
        );

      if (getModuleQuestionsWithFiltersResponse.status === 200) {
        setLoading('completed');

        const multiSubjectSectionMetaInfo =
          getModuleQuestionsWithFiltersResponse?.data
            ?.multiSubjectSectionMetaInfo || [];
        const responseSection = multiSubjectSectionMetaInfo[0];

        const hasPassageSection =
          responseSection?.multiSubjectSubSections?.some(
            (subSection) =>
              subSection.subSectionType ===
              QpgSubsectionTypeEnum.QPG_SUBSECTION_TYPE_PASSAGE
          );

        if (responseSection) {
          if (hasPassageSection) {
            const chapterTopicsInfo =
              responseSection.chapterInfo?.chapterTopicsInfo ?? [];
            responseSection.chapterInfo = new ChapterInfo({
              chapterTopicsInfo: chapterTopicsInfo.map(
                (chapter) =>
                  new ChapterIdsInfo({
                    ...chapter,
                    noOfQuestions: 0,
                    topicInfo: (chapter.topicInfo ?? []).map((topic) => ({
                      ...topic,
                      noOfQuestions: 0,
                    })),
                  })
              ),
            });
          } else {
            responseSection.chapterInfo =
              getUpdatedSectionChapterInfoFromQuestionModuleMap({
                section: responseSection,
                questionModuleMap:
                  getModuleQuestionsWithFiltersResponse?.data
                    ?.questionModuleMap,
              });
          }
        }

        // Only one section data is coming and is incomplete
        // complete current section data using qpg_content and response
        // and preserve other sections data from qpg_content
        const metaInfo =
          qpg_content?.multiSubjectSections?.map(
            (section: MultiSubjectSection) => {
              const isSelectedSection =
                section.sectionNo === qpg_selected_section_number;

              // Redux sections are plain objects — re-wrap for Connect serialization
              if (!isSelectedSection) {
                return new MultiSubjectSection(section);
              }

              if (!responseSection) {
                return new MultiSubjectSection(section);
              }

              const rebuiltSubSections =
                responseSection.multiSubjectSubSections.map(
                  (subSection, subSectionIndex: number) => {
                    const subsectionFromState =
                      qpg_subsection_data?.[subSectionIndex];
                    const responseSubSection =
                      responseSection.multiSubjectSubSections[subSectionIndex];
                    const passageImagesUpload = (
                      subsectionFromState?.passageImagesUpload ??
                      subSection.passageImagesUpload ??
                      []
                    ).map((upload) => new CloudUpload(upload));
                    const attemptAnyEnabled =
                      subsectionFromState?.attemptAnyCount !== undefined;
                    const questionTypeAndMarks =
                      subsectionFromState?.subSectionQuestions.map(
                        (question) => {
                          const { perChildMarks } =
                            getEffectiveMarksForSubSectionQuestion({
                              marks: question.marks ?? 0,
                              noOfQuestion: question.noOfQuestion ?? 0,
                              isGrouped: question.isGrouped,
                              attemptAnyEnabled,
                              groupQuestionAttemptedAny:
                                question.groupQuestionAttemptedAny,
                            });
                          return new QuestionTypeMarks({
                            questionType: question.questionType,
                            marks: toRequestMarks(perChildMarks),
                          });
                        }
                      ) || [];
                    const subSectionMetaData = (
                      subSection.subSectionMetaData ?? []
                    ).map((meta, metaIndex) => {
                      const questionFromState =
                        subsectionFromState?.subSectionQuestions?.[metaIndex];
                      if (!questionFromState) {
                        return new SectionQuestionMetaData(meta);
                      }
                      const { perChildMarks } =
                        getEffectiveMarksForSubSectionQuestion({
                          marks: questionFromState.marks ?? 0,
                          noOfQuestion: questionFromState.noOfQuestion ?? 0,
                          isGrouped: questionFromState.isGrouped,
                          attemptAnyEnabled,
                          groupQuestionAttemptedAny:
                            questionFromState.groupQuestionAttemptedAny,
                        });
                      const groupedQuestions = (
                        meta.groupedQuestions ?? []
                      ).map(
                        (child) =>
                          new SectionQuestionMetaData({
                            ...child,
                            maxMarks: perChildMarks,
                          })
                      );
                      const nextMeta = new SectionQuestionMetaData({
                        ...meta,
                        maxMarks: perChildMarks,
                        groupedQuestions,
                        ...(questionFromState.isGrouped !== undefined
                          ? { isGrouped: questionFromState.isGrouped }
                          : meta.isGrouped !== undefined
                          ? { isGrouped: meta.isGrouped }
                          : {}),
                        ...(questionFromState.groupQuestionHeading?.trim()
                          ? {
                              groupQuestionHeading:
                                questionFromState.groupQuestionHeading,
                            }
                          : meta.groupQuestionHeading?.trim()
                          ? {
                              groupQuestionHeading: meta.groupQuestionHeading,
                            }
                          : {}),
                        ...(questionFromState.groupQuestionAttemptedAny !==
                        undefined
                          ? {
                              groupQuestionAttemptedAny:
                                questionFromState.groupQuestionAttemptedAny,
                            }
                          : {}),
                      });
                      return withSeededGroupedQuestionHeading(
                        nextMeta,
                        questionFromState.questionType ?? meta.questionType
                      );
                    });

                    return new MultiSubjectSubSection({
                      ...subSection,
                      questionTypeAndMarks,
                      subSectionMetaData,
                      subSectionType:
                        subsectionFromState?.subSectionType ??
                        subSection.subSectionType,
                      passageText: responseSubSection?.passageText ?? [],
                      passageImagesUpload,
                      isAiGeneratedPassage:
                        responseSubSection?.isAiGeneratedPassage,
                      ...(subsectionFromState?.attemptAnyCount !== undefined
                        ? {
                            attemptAnyCount:
                              subsectionFromState.attemptAnyCount,
                          }
                        : {}),
                    });
                  }
                );

              return new MultiSubjectSection({
                ...responseSection,
                multiSubjectSubSections: rebuiltSubSections,
                multiSubjectSectionStatus:
                  MultiSubjectSectionStatusEnum.MULTI_SUBJECT_SECTION_STATUS_DRAFT,
                preferredMarks: section.preferredMarks,
                sectionNo: section.sectionNo,
                sectionTitle: section.sectionTitle,
                sectionName: section.sectionName,
                isAiGenerated: !!qpg_settings_config?.allow_ai_questions,
                totalMarks: getTotalMarks(),
              });
            }
          ) || [];

        const qpgChapters =
          qpg_content?.assessment?.taskMetaInfo?.chapterInfo
            ?.chapterTopicsInfo || [];
        const moduleChapters =
          getModuleQuestionsWithFiltersResponse.data?.taskMetaInfo?.chapterInfo
            ?.chapterTopicsInfo || [];

        const assessmentCreationSubmitResponse =
          await LmsAssessmentTeacherAPIServiceV6WithStatusCodeHandler.assessmentCreationSubmit(
            {
              teacherId: BigInt(teacherId),
              assessmentId: qpg_content?.assessment?.assessmentId,
              classId: qpg_content?.assessment?.classId,
              creationStatus: TaskCreationStatusEnum.TASK_CREATION_STATUS_DRAFT,
              multiSubjectSectionMetaInfo: metaInfo,
              sectionId: qpg_content?.assessment?.sectionId,
              subjectIds:
                qpg_content?.multiSubjectSections.map(
                  (section) => section.subjectId
                ) ?? [],
              taskName: qpg_content?.assessment?.assessmentTitle,
              taskType: TaskEnum.TASK_MULTI_SUBJECT_QUESTION_PAPER_CUSTOM,
              instructions: qpg_content?.assessment?.assessmentInstructions,
              questionModuleMap: (() => {
                const combined = [
                  ...(qpg_content?.questionsSequenceInfo?.questionModuleMap ??
                    []),
                  ...(getModuleQuestionsWithFiltersResponse?.data
                    ?.questionModuleMap ?? []),
                ];
                const seen = new Set<string>();
                return combined
                  .filter((m) => {
                    const key = `${m.questionId ?? ''}|${m.chapterId ?? ''}|${
                      m.topicId ?? ''
                    }`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  })
                  .map((m) => new QuestionModuleMap(m));
              })(),
              selectedTasksInfo: buildTaskInfoModels(metaInfo),
              chapterIdsInfo: mergeChapterTopicsInfoUnion(
                qpgChapters,
                moduleChapters
              ),
            }
          );

        // const content =
        //   await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentContent(
        //     {
        //       assessmentId:
        //         assessmentCreationSubmitResponse.data?.assessment?.assessmentId,
        //     }
        //   );

        if (assessmentCreationSubmitResponse.status === 200) {
          setLoading('completed');
          const contentWithSections = cloneObject(qpg_content);
          if (contentWithSections) {
            contentWithSections.multiSubjectSections = metaInfo;
          }
          dispatch(setQPGContent(contentWithSections));
          dispatch(
            setQPGCreatedSectionTest(getModuleQuestionsWithFiltersResponse.data)
          );
          navigate(`${QPG_ASSESSMENT_REVIEW_PAPER}`);
        } else {
          setLoading('error');
          dispatch(
            setToastInfo({
              label: 'Error while creating assessment',
              variant: 'error',
              open: true,
            })
          );
        }
      } else {
        setLoading('error');
        dispatch(
          setToastInfo({
            label: 'Error while creating assessment',
            variant: 'error',
            open: true,
          })
        );
      }
    } catch (error) {
      setLoading('error');
      console.log('error:', error);
      let message = 'Error while creating assessment';
      if (error instanceof ConnectError) {
        message = error.rawMessage?.replace(/.*\]\s*/, '').trim();
      }
      dispatch(
        setToastInfo({
          label: message,
          variant: 'error',
          open: true,
        })
      );
    }
  };

  // --------------------------------------------------------------

  // const disableChapterSelectionStep = () => {
  //   return (
  //     !qpg_selected_chapter_data?.chapters ||
  //     qpg_selected_chapter_data?.chapters?.length === 0
  //   );
  // };

  // const disableSettingsStep = () => {
  //   return (
  //     disableChapterSelectionStep() ||
  //     qpg_settings_config == undefined ||
  //     qpg_settings_config.bloomLevels?.length === 0
  //   );
  // };

  // const disableAddQuestionTypeStep = () => {
  //   const allSubsectionsFilled = qpg_subsection_data?.every((subsection) =>
  //     areAllSubSectionQuestionsFilled(subsection.subSectionQuestions)
  //   );
  //   return (
  //     disableChapterSelectionStep() ||
  //     disableSettingsStep() ||
  //     qpg_subsection_data?.length === 0 ||
  //     !allSubsectionsFilled
  //   );
  // };

  const disableSettingsStep = () => {
    return (
      !qpg_selected_chapter_data?.chapters ||
      qpg_selected_chapter_data?.chapters?.length === 0
    );
  };

  const disableAddQuestionTypeStep = () => {
    return (
      disableSettingsStep() ||
      qpg_settings_config == undefined ||
      qpg_settings_config.bloomLevels?.length === 0
    );
  };

  const isStep2NextDisabled = () => {
    if (!qpg_subsection_data?.length) return true;
    const allSubsectionsFilled = qpg_subsection_data.every(
      (sub) =>
        (sub.subSectionQuestions?.length ?? 0) > 0 &&
        areAllSubSectionQuestionsFilled(sub.subSectionQuestions)
    );
    if (!allSubsectionsFilled) return true;

    const hasInvalidManualPassage = qpg_subsection_data.some(
      (sub) =>
        isPassageSectionEnabled(sub) &&
        !sub.isAiGeneratedPassage &&
        !isPassageTextMinLengthMet(sub.passageText?.[0])
    );
    if (hasInvalidManualPassage) return true;

    if (qpg_settings_config?.allow_ai_questions) {
      return (
        !!qpg_settings_config?.prompt?.trim()?.length &&
        qpg_settings_config?.prompt_validated !== 'success'
      );
    }
    return false;
  };

  const isNextButtonDisabled = () => {
    if (selectedStepIndex === 2) {
      return isStep2NextDisabled();
    }
    return steps[selectedStepIndex + 1]?.disableStep ?? false;
  };

  const isCreateBlockedByUnvalidatedPrompt =
    selectedStepIndex === 2 &&
    !!qpg_settings_config?.allow_ai_questions &&
    !!qpg_settings_config?.prompt?.trim()?.length &&
    qpg_settings_config?.prompt_validated !== 'success';

  // --------------------------------------------------------------

  const getPreferredMarks = (): number => {
    const data = qpg_content?.multiSubjectSections?.find(
      (e) => e.sectionNo === qpg_selected_section_number
    );

    return data?.preferredMarks ?? 0;
  };

  // --------------------------------------------------------------

  const getTotalMarks = (): number => {
    const data = qpg_subsection_data ?? [];
    return data.reduce(
      (sectionAcc, subsection) =>
        sectionAcc + getQpgCreationSubSectionMarksTotal(subsection),
      0
    );
  };

  // --------------------------------------------------------------

  const steps: ISteps[] = [
    {
      index: 0,
      stepTitle: 'Chapter Selection',
      stepClickHandler: async () => {
        // dispatch(setQPGSettingsConfig(undefined));
        // dispatch(setCurrentQPGTemplateInfo(undefined));
        // dispatch(setQPGGeneratedTemplates(undefined));
        setSelectedStepIndex(0);
        console.log('Step 1 Clicked');
        // await interactionEvent({
        //   context: 'Question_paper_generator',
        //   name: 'CHAPTER_SELECTION',
        //   pathSegments: pathSegments,
        //   isOffline: isOffline,
        // });
      },
      stepButtonText: 'NEXT',
      disableStep: false,
      stepIconColor: '#007CDC',
      disabledIconColor: '#7EC6FD',
      // element: <ChapterSelection />,
    },

    {
      index: 1,
      stepTitle: 'Settings',
      stepClickHandler: async () => {
        // dispatch(setCurrentQPGTemplateInfo(undefined));
        // dispatch(setQPGGeneratedTemplates(undefined));
        setSelectedStepIndex(1);
        console.log('Step 2 Clicked');
        // await interactionEvent({
        //   context: 'Question_paper_generator',
        //   name: 'SETTINGS',
        //   pathSegments: pathSegments,
        //   isOffline: isOffline,
        // });
      },
      stepButtonText: 'NEXT',
      // disableStep:
      //   !qpg_selected_chapter_data?.chapters ||
      //   qpg_selected_chapter_data?.chapters?.length === 0,
      disableStep: disableSettingsStep(),
      stepIconColor: '#0AA34F',
      disabledIconColor: '#44FD97',
      // element: isNaN(Number(subject_id)) ? (
      //   <></>
      // ) : (
      //   <Settings subjectId={Number(subject_id)} />
      // ),
    },
    {
      index: 2,
      stepTitle: 'Choose Question Type',
      stepClickHandler: async () => {
        setSelectedStepIndex(2);
        // await interactionEvent({
        //   context: 'Question_paper_generator',
        //   name: 'ADD_SECTION',
        //   pathSegments: pathSegments,
        //   isOffline: isOffline,
        // });
      },
      stepButtonText: 'CREATE TEST',
      // disableStep:
      //   qpg_settings_config == undefined ||
      //   (Object.values(
      //     qpg_settings_config?.no_ofQuestions_by_types || {}
      //   ).reduce((acc, curr) => acc + (curr || 0), 0) === 0 &&
      //     !qpg_settings_config.allow_ai_questions) ||
      //   (current_qpg_template_info?.questionSectionInfo?.some(
      //     (section) => section.marks === 0
      //   ) ??
      //     false),
      disableStep: disableAddQuestionTypeStep(),

      // if no questions available in db and AI is disabled then disable the step
      // also disable if any section has marks as 0
      stepIconColor: '#F8C807',
      disabledIconColor: '#FEEDAE',
      // element: (
      //   <AddSection
      //     isTemplateValid={isTemplatevalid}
      //     setIsTemplateValid={setIsTemplateValid}
      //     subjectId={Number(subject_id)}
      //   />
      // ),
    },
  ];

  // --------------------------------------------------------------

  const handleNextClick = async () => {
    // if (selectedStepIndex === steps.length - 1) {
    //   setCreateAssessmentPopupOpen(true);
    // } else {
    //   setSelectedStepIndex(selectedStepIndex + 1);
    // }

    if (selectedStepIndex === steps.length - 1) {
      if (getPreferredMarks() === getTotalMarks()) {
        createTestHandler();
      } else {
        setConfirmCreateAssessmentPopup(true);
      }
    } else {
      setSelectedStepIndex(selectedStepIndex + 1);
    }

    // await interactionEvent({
    //   context: 'Question_paper_generator',
    //   name: (steps[selectedStepIndex]?.stepButtonText || 'NEXT')
    //     .toUpperCase()
    //     .replace(/\s+/g, '_'),
    //   pathSegments: pathSegments,
    //   isOffline: isOffline,
    // });
  };

  // --------------------------------------------------------------

  // When entering this step: if qpg_selected_chapter_data is empty but qpg_content has chapter data for the current section, restore it
  const restoreSelectedChapterData = () => {
    const selectedChaptersLength =
      qpg_selected_chapter_data?.chapters?.length ?? 0;
    const hasSelectedChapterData = selectedChaptersLength > 0;
    if (hasSelectedChapterData || !qpg_content?.multiSubjectSections?.length) {
      return;
    }
    const section = qpg_content.multiSubjectSections.find(
      (s) => s.sectionNo === qpg_selected_section_number
    );
    const sectionChapterTopics = section?.chapterInfo?.chapterTopicsInfo;
    const chapterTopicsInfo =
      sectionChapterTopics && sectionChapterTopics.length > 0
        ? sectionChapterTopics
        : undefined;
    if (!chapterTopicsInfo?.length || !section) return;

    const chapters: SubjectChapterMiniInfo[] = chapterTopicsInfo.map((c) => {
      const topics = (c.topicInfo ?? []).map(
        (t) =>
          new TopicBasicInfo({
            topicId: t.topicId,
            topicNo: t.topicNo,
            topicTitle: t.name ?? '',
          })
      );
      return new SubjectChapterMiniInfo({
        chapterId: c.chapterId,
        chapterNo: c.chapterNo,
        chapterTitle: c.name ?? '',
        noOfTopics: topics.length,
        topics,
      });
    });
    const sortedChapters = [...chapters].sort(
      (a, b) => (a.chapterNo ?? 0) - (b.chapterNo ?? 0)
    );
    const courseIndexInfo = new CourseIndexInfo({
      subjectId: section.subjectId ?? 0,
      subjectName: section.sectionName ?? '',
      chapters: sortedChapters,
    });
    dispatch(setQPGSelectedChapterData(courseIndexInfo));
    // setSelectedChapterList(sortedChapters);
  };

  // --------------------------------------------------------------

  useEffect(() => {
    restoreSelectedChapterData();
  }, [
    JSON.stringify(qpg_content),
    qpg_selected_section_number,
    qpg_selected_chapter_data?.chapters?.length,
    dispatch,
  ]);

  const backClickHandler = () => {
    setExitConfirmPopup('back');
  };
  const logoClickHandler = () => {
    setExitConfirmPopup('logo');
  };

  useEffect(() => {
    setSelectedFunction(() => backClickHandler);
    setSelectedLogoClickHandler(() => logoClickHandler);
    return () => {
      setSelectedFunction(null);
      setSelectedLogoClickHandler(null);
    };
  }, [exitConfirmPopup]);

  // --------------------------------------------------------------

  if (loading === 'loading') return <Loader />;

  return (
    <Box sx={styles.root}>
      <Box
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          // alignItems: 'center',
          marginY: { xs: pxToRem(15), md: pxTovW(20) },
          display: 'flex',
        }}
      >
        <Typography variant="h1">Question Paper Generator</Typography>
        <Tooltip
          title={
            isCreateBlockedByUnvalidatedPrompt
              ? 'Please validate your prompt first'
              : ''
          }
          arrow
          disableHoverListener={!isCreateBlockedByUnvalidatedPrompt}
        >
          <Box
            component="span"
            sx={{
              ...styles.buttonContainer,
              display: 'inline-flex',
              cursor: isCreateBlockedByUnvalidatedPrompt
                ? 'not-allowed'
                : undefined,
            }}
          >
            <PrimaryButton
              sx={{ width: '100%', height: '100%', minWidth: 'unset' }}
              disabled={isNextButtonDisabled()}
              onClick={handleNextClick}
            >
              <Typography variant="h3" color="common.white" fontWeight="bold">
                {steps[selectedStepIndex]?.stepButtonText || 'Next'}
              </Typography>
            </PrimaryButton>
          </Box>
        </Tooltip>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: { xs: pxToRem(40), md: pxTovW(60) },
          marginBottom: { xs: pxToRem(30), md: pxTovW(50) },
        }}
      >
        <StepperComponent currentStep={selectedStepIndex} steps={steps} />
      </Box>

      {selectedStepIndex === 0 ? (
        <QPGCreationChapterSelection />
      ) : selectedStepIndex === 1 ? (
        <QPGCreationSettings />
      ) : (
        <QPGCreationAddQuestionType
          isTemplateValid={isTemplateValid}
          setIsTemplateValid={setIsTemplateValid}
          subjectId={Number(subjectId)}
          totalMarks={getTotalMarks()}
          preferredMarks={getPreferredMarks()}
        />
      )}

      <ActionsPopup
        open={confirmCreateAssessmentPopup}
        handleClose={() => {
          setConfirmCreateAssessmentPopup(false);
        }}
        fontSmall
        iconName="error_yellow"
        popupText={`Total marks (${getTotalMarks()}) don't match Preferred marks (${getPreferredMarks()}).`}
        splitText="Do you want to create assessment?"
        yesClickHandler={async () => {
          createTestHandler();
          setConfirmCreateAssessmentPopup(false);
        }}
        noClickHandler={async () => {
          setConfirmCreateAssessmentPopup(false);
        }}
      />

      <ActionsPopup
        open={!!exitConfirmPopup}
        handleClose={() => {
          setExitConfirmPopup(undefined);
        }}
        fontSmall
        iconName="error_yellow"
        popupText="If you go back, you may lose your changes."
        splitText="Are you sure you want go back?"
        yesClickHandler={async () => {
          if (exitConfirmPopup === 'back') {
            navigate(
              QPG_ASSESSMENT_PUBLISH +
                `?assessment_id=${qpg_content?.assessment?.assessmentId}`
            );
          } else {
            navigate(HOME);
          }
          setExitConfirmPopup(undefined);
        }}
        noClickHandler={async () => {
          setExitConfirmPopup(undefined);
        }}
      />
    </Box>
  );
};
